<template>
  <div
    v-if="visible"
    class="quick-mark-payment-paid-modal modal fade show"
    style="display: block; background-color: rgba(0, 0, 0, 0.5)"
    tabindex="-1"
  >
    <div class="modal-dialog modal-md">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="fas fa-bolt me-2 text-warning"></i>
            {{ $t("financialSummary.stages.quickMarkPaymentPaidTitle") }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <p class="small text-muted mb-3">
            {{ $t("financialSummary.stages.quickMarkPaymentPaidHint") }}
          </p>

          <div v-if="selectedPayment" class="mb-3 p-2 rounded bg-light">
            <div class="row g-2 small">
              <div class="col-12">
                <span class="text-muted">{{ $t("financialSummary.name") }}:</span>
                <span class="fw-semibold ms-1">{{ selectedPayment.name || "—" }}</span>
              </div>
            </div>
          </div>

          <div v-if="serverValidationError" class="alert alert-danger mb-3">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ serverValidationError }}
          </div>

          <form @submit.prevent="submitQuickPay">
            <div class="mb-3">
              <label class="form-label">
                {{ $t("financialSummary.stages.quickMarkPaymentPaidAmountLabel") }}
                <span class="text-danger">*</span>
              </label>
              <input
                v-model="form.amountStr"
                type="text"
                inputmode="decimal"
                class="form-control"
                :class="{ 'is-invalid': errors.amount }"
                autocomplete="off"
              />
              <div v-if="errors.amount" class="invalid-feedback d-block">
                {{ errors.amount }}
              </div>
              <small v-if="expenseAmountCap != null" class="text-muted d-block mt-1">
                {{
                  $t("financialSummary.stages.quickMarkPaymentPaidAmountMaxHint", {
                    max: formatMoneyHint(expenseAmountCap),
                  })
                }}
              </small>
            </div>

            <!-- Rounding / comisión de la línea appliedto. Solo con una línea: con varias
                 no hay forma de saber a cuál aplicarlos, y ahí va la pantalla completa. -->
            <div v-if="appliedToExtrasEditable" class="row g-2 mb-3">
              <div class="col-6">
                <label class="form-label small mb-1" for="qmp-appliedto-rounding">{{
                  $t("financialSummary.quickAddCollectAppliedtoRounding")
                }}</label>
                <input
                  id="qmp-appliedto-rounding"
                  v-model="form.lineRoundingStr"
                  type="text"
                  inputmode="decimal"
                  class="form-control form-control-sm"
                  autocomplete="off"
                />
              </div>
              <div class="col-6">
                <label class="form-label small mb-1" for="qmp-appliedto-commission">{{
                  $t("financialSummary.quickAddCollectAppliedtoCommission")
                }}</label>
                <input
                  id="qmp-appliedto-commission"
                  v-model="form.lineCommissionStr"
                  type="text"
                  inputmode="decimal"
                  class="form-control form-control-sm"
                  autocomplete="off"
                />
              </div>
            </div>

            <div v-if="!editMode" class="mb-3">
              <label class="form-label">
                {{ $t("financialSummary.stages.quickMarkPaymentPaidDateLabel") }}
                <span v-if="!estimatedOnlyMode" class="text-danger">*</span>
              </label>
              <input
                :value="form.payment_date"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.payment_date }"
                @input="onPaymentDateInput"
              />
              <div v-if="errors.payment_date" class="invalid-feedback d-block">
                {{ errors.payment_date }}
              </div>
            </div>

            <div v-if="!editMode" class="mb-3">
              <label class="form-label">
                {{ $t("financialSummary.stages.quickMarkPaymentPaidEstimatedDateLabel") }}
                <span v-if="estimatedOnlyMode" class="text-danger">*</span>
              </label>
              <input
                :value="form.estimated_paymentdate"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.estimated_paymentdate }"
                @input="onEstimatedDateInput"
              />
              <div v-if="errors.estimated_paymentdate" class="invalid-feedback d-block">
                {{ errors.estimated_paymentdate }}
              </div>
            </div>

            <div v-if="showBankAccountField" class="mb-3">
              <label class="form-label">
                {{ $t(bankAccountField.label || "Bank account (from)") }}
                <span v-if="bankAccountRequired" class="text-danger">*</span>
              </label>
              <FieldReference
                :field="bankAccountField"
                :value="form.bankaccount"
                :referenceOptions="referenceOptions.bankaccount || []"
                mode="edit"
                :showLabel="false"
                @input="onBankAccountChange"
              />
              <div v-if="errors.bankaccount" class="invalid-feedback d-block">
                {{ errors.bankaccount }}
              </div>
            </div>

            <div v-if="showBankAccountToField" class="mb-3">
              <label class="form-label">
                {{ $t(bankAccountToField.label || "Bank account (to)") }}
              </label>
              <FieldReference
                :field="bankAccountToField"
                :value="form.bank_account_to"
                :referenceOptions="referenceOptions.bankaccount || []"
                mode="edit"
                :showLabel="false"
                @input="onBankAccountToChange"
              />
              <div v-if="errors.bank_account_to" class="invalid-feedback d-block">
                {{ errors.bank_account_to }}
              </div>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ $t("financialSummary.stages.quickMarkPaymentPaidCommentLabel") }}</label>
              <textarea
                v-model="form.comment"
                class="form-control"
                rows="2"
                :placeholder="$t('financialSummary.stages.quickMarkPaymentPaidCommentPlaceholder')"
              ></textarea>
            </div>

            <div class="mb-3">
              <label class="form-label">{{ $t("financialSummary.stages.quickMarkPaymentPaidReceiptLabel") }}</label>
              <div class="input-group">
                <input
                  ref="fileInput"
                  type="file"
                  class="form-control"
                  accept="image/*,.pdf,.doc,.docx"
                  :disabled="uploadingFile"
                  @change="handleFileUpload"
                />
                <button
                  v-if="formPaymentRequestFiles.length"
                  type="button"
                  class="btn btn-outline-danger"
                  :disabled="uploadingFile"
                  @click="removeFile"
                >
                  <i class="fas fa-times"></i>
                </button>
              </div>
              <div v-if="uploadingFile" class="mt-2">
                <div class="progress">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    :style="{ width: uploadProgress + '%' }"
                  >
                    {{ uploadProgress }}%
                  </div>
                </div>
              </div>
              <div
                v-for="(reqFile, reqIdx) in formPaymentRequestFiles"
                :key="reqIdx"
                class="mt-2 small text-success"
              >
                <i class="fas fa-file me-1"></i>{{ getFileName(reqFile) }}
              </div>
              <small class="text-muted">{{ $t("financialSummary.stages.quickMarkPaymentPaidReceiptHint") }}</small>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            {{ $t("Cancel") }}
          </button>
          <button
            v-if="editMode"
            type="button"
            class="btn btn-outline-danger me-auto"
            :disabled="saving || deleting || !canDeleteSelectedPayment"
            @click="removePayment"
          >
            <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else class="fas fa-trash-alt me-2"></i>
            {{ deleting ? $t("Updating...") : $t("Delete") }}
          </button>
          <button
            type="button"
            class="btn btn-success"
            :disabled="saving || deleting"
            @click="submitQuickPay"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <i v-else class="fas fa-check me-2"></i>
            {{
              saving
                ? $t("Updating...")
                : editMode
                  ? $t("Save")
                : estimatedOnlyMode
                  ? $t("Save")
                  : $t("financialSummary.stages.quickMarkPaymentPaidSave")
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import FieldReference from "@/components/gotrading/model-definition-core/FieldReference.vue";
import Swal from "sweetalert2";
import { formatNumberSync } from "@/utils/numberFormatter";
import { toYyyyMmDdSync, calendarTodayLocalYyyyMmDd } from "@/utils/dateFormatter";
import financialReceiptFilesMixin from "@/mixins/financialReceiptFilesMixin";
import {
  isPaymentUpdateTransportError,
  verifyPaymentStatusPersisted,
} from "@/utils/paymentMarkPaidUpdate";
import { loadPaymentAppliedToRows } from "@/utils/paymentAppliedToLoad";
import permissionsMixin from "@/mixins/permissionsMixin";
import {
  canDeletePaymentsFor,
  confirmAndDeletePayment,
} from "@/utils/paymentDeleteFlow";

function parseAmountInput(raw) {
  if (raw === "" || raw === null || raw === undefined) return NaN;
  const n = parseFloat(String(raw).replace(",", ".").trim());
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Rounding / bank commission de la línea appliedto.
 * Vacío o ilegible se guarda como 0, igual que en los modales de creación.
 */
function parseLineExtra(raw) {
  const n = parseAmountInput(raw);
  return Number.isFinite(n) ? n : 0;
}

/** El 0 se muestra vacío para no llenar el formulario de ceros sin información. */
function lineExtraToStr(value) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return String(n);
}

function bankRefId(val) {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object" && val._id) return String(val._id);
  return "";
}

export default {
  name: "QuickMarkPaymentPaidModal",
  components: { FieldReference },
  mixins: [permissionsMixin, financialReceiptFilesMixin],
  data() {
    return {
      visible: false,
      selectedPayment: null,
      /**
       * Filas payment_appliedto del pago abierto y si la carga fue completa.
       * El PUT borra las filas de grupo que no lleguen en el body, así que sin
       * carga completa no se manda appliedto (ver utils/paymentAppliedToLoad).
       */
      appliedToRows: [],
      appliedToComplete: false,
      form: {
        amountStr: "",
        payment_date: "",
        estimated_paymentdate: "",
        bankaccount: "",
        bank_account_to: "",
        comment: "",
        payment_request_file: [],
        lineRoundingStr: "",
        lineCommissionStr: "",
      },
      initialSnapshot: null,
      errors: {},
      serverValidationError: "",
      saving: false,
      deleting: false,
      uploadingFile: false,
      uploadProgress: 0,
      paymentFields: [],
      bankAccountField: null,
      bankAccountToField: null,
      referenceOptions: {},
      /** `shipping_expenses.amount` del gasto vinculado (una sola línea appliedto); null = sin tope en UI. */
      expenseAmountCap: null,
      editMode: false,
    };
  },
  computed: {
    /**
     * Única línea appliedto del pago, o null. Con más de una no se puede saber a cuál
     * aplicar rounding/comisión, y sin carga completa mandar el grupo borraría filas.
     */
    singleAppliedToLine() {
      if (!this.appliedToComplete) return null;
      return this.appliedToRows.length === 1 ? this.appliedToRows[0] : null;
    },
    appliedToExtrasEditable() {
      return !!(this.singleAppliedToLine && this.singleAppliedToLine._id);
    },
    estimatedOnlyMode() {
      return !!(this.form.estimated_paymentdate && String(this.form.estimated_paymentdate).trim());
    },
    selectedPaymentIsPaid() {
      const p = this.selectedPayment;
      if (!p) return false;
      const paid = p.paid;
      return paid === true || paid === "true" || paid === 1 || paid === "1";
    },
    showBankAccountField() {
      if (!this.bankAccountField) return false;
      if (this.editMode) {
        return this.selectedPaymentIsPaid || !!bankRefId(this.form.bankaccount);
      }
      return !this.estimatedOnlyMode;
    },
    showBankAccountToField() {
      if (!this.bankAccountToField) return false;
      if (this.editMode) {
        return (
          this.selectedPaymentIsPaid ||
          !!bankRefId(this.form.bankaccount) ||
          !!bankRefId(this.form.bank_account_to)
        );
      }
      return !this.estimatedOnlyMode;
    },
    bankAccountRequired() {
      return this.showBankAccountField && !this.editMode && !this.estimatedOnlyMode;
    },
    /** El endpoint de borrado permanente pide `payment.manage` (o admin). */
    canDeleteSelectedPayment() {
      if (!this.selectedPayment) return false;
      return canDeletePaymentsFor((perm) => this.hasPermission(perm));
    },
    formPaymentRequestFiles() {
      return this.normalizePaymentRequestFileList(this.form.payment_request_file);
    },
  },
  methods: {
    formatMoneyHint(n) {
      return "$" + formatNumberSync(n || 0, null, true);
    },
    async loadPaymentFields() {
      try {
        const response = await wencesApi.get("/api/financial/payments/fields");
        if (response.data && response.data.success) {
          this.paymentFields = response.data.data || [];
          this.bankAccountField = this.paymentFields.find((f) => f.key === "bankaccount") || null;
          if (this.bankAccountField) this.bankAccountField.preload = true;
          this.bankAccountToField =
            this.paymentFields.find((f) => f && f.key === "bank_account_to") || null;
          if (this.bankAccountToField) this.bankAccountToField.preload = true;
        }
      } catch (e) {
        console.error("QuickMarkPaymentPaidModal loadPaymentFields:", e);
      }
    },
    async loadBankOptions() {
      const refField = this.bankAccountField || this.bankAccountToField;
      if (!refField || !refField.ref) return;
      try {
        const params = new URLSearchParams();
        params.append("perPage", "1000");
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${refField.ref}?${params.toString()}`
        );
        const items = (data && (data.items || data.data || data)) || [];
        this.$set(
          this.referenceOptions,
          "bankaccount",
          Array.isArray(items) ? items.filter((x) => x && (x._id || x.name)) : []
        );
      } catch (e) {
        console.error("QuickMarkPaymentPaidModal loadBankOptions:", e);
      }
    },
    formatPaymentDateForInput(payment) {
      if (!payment || !payment.payment_date) return "";
      return toYyyyMmDdSync(payment.payment_date);
    },
    onPaymentDateInput(event) {
      const v = event && event.target ? event.target.value : "";
      this.form.payment_date = v;
      if (v) this.form.estimated_paymentdate = "";
      if (this.errors.payment_date) delete this.errors.payment_date;
      if (this.errors.estimated_paymentdate) delete this.errors.estimated_paymentdate;
    },
    onEstimatedDateInput(event) {
      const v = event && event.target ? event.target.value : "";
      this.form.estimated_paymentdate = v;
      if (v) this.form.payment_date = "";
      if (this.errors.estimated_paymentdate) delete this.errors.estimated_paymentdate;
      if (this.errors.payment_date) delete this.errors.payment_date;
    },
    bankFromPayment(payment, key = "bankaccount") {
      const b = payment && payment[key];
      if (!b) return "";
      if (typeof b === "object" && b._id) return b;
      if (typeof b === "string") return { _id: b, name: b };
      return "";
    },
    /**
     * appliedto para el PUT: la lista completa, con rounding y bank_commission
     * escritos solo en la línea editada. Devuelve null si no hay nada que cambiar,
     * para no reescribir el grupo (y disparar sus hooks) sin motivo.
     */
    buildAppliedToForPut() {
      const line = this.singleAppliedToLine;
      if (!line || !line._id) return null;
      const snap = this.initialSnapshot || {};
      const unchanged =
        String(this.form.lineRoundingStr ?? "") === String(snap.lineRoundingStr ?? "") &&
        String(this.form.lineCommissionStr ?? "") === String(snap.lineCommissionStr ?? "");
      if (unchanged) return null;
      return [
        {
          _id: line._id,
          rounding: parseLineExtra(this.form.lineRoundingStr),
          bank_commission: parseLineExtra(this.form.lineCommissionStr),
        },
      ];
    },
    async loadExpenseAmountCap(payment) {
      this.expenseAmountCap = null;
      this.appliedToRows = [];
      this.appliedToComplete = false;
      if (!payment || !payment._id) return;
      try {
        const { rows, complete } = await loadPaymentAppliedToRows(wencesApi, payment);
        this.appliedToRows = Array.isArray(rows) ? rows : [];
        this.appliedToComplete = complete === true;
        if (!Array.isArray(rows) || rows.length !== 1) return;
        const row = rows[0];
        const expRef = row.expense;
        const expId =
          expRef && typeof expRef === "object" && expRef._id != null
            ? String(expRef._id)
            : expRef != null
              ? String(expRef).trim()
              : "";
        if (!expId) return;
        const resExp = await wencesApi.get(
          `/api/gotrading/dynamic/shipping_expenses/${encodeURIComponent(expId)}`
        );
        const expWrap = resExp && resExp.data ? resExp.data : null;
        const exp =
          expWrap && typeof expWrap === "object" && expWrap.data != null && typeof expWrap.data === "object"
            ? expWrap.data
            : expWrap;
        if (!exp || exp.amount == null || exp.amount === "") return;
        const cap = Number(exp.amount);
        if (Number.isFinite(cap) && cap >= 0) {
          this.expenseAmountCap = cap;
        }
      } catch (e) {
        console.warn("QuickMarkPaymentPaidModal loadExpenseAmountCap:", e.message);
      }
    },
    async open(payment, options = {}) {
      if (!payment || !payment._id) return;
      this.serverValidationError = "";
      this.errors = {};
      this.editMode = !!(options && options.editMode === true);
      await this.loadPaymentFields();
      await this.loadBankOptions();

      let p = payment;
      const incomingBank = payment.bankaccount;
      const incomingBankTo = payment.bank_account_to;
      try {
        const response = await wencesApi.get(`/api/gotrading/dynamic/payment/${payment._id}`);
        if (response.data && response.data.data) {
          const fetched = response.data.data || {};
          const getRefId = (val) => bankRefId(val);
          const hasLabel = (val) =>
            !!(val && typeof val === "object" && (String(val.name || "").trim() || String(val.label || "").trim()));
          const mergeBankRef = (incoming, fetchedVal, key) => {
            const incId = getRefId(incoming);
            const fetId = getRefId(fetchedVal);
            if (incoming && incId && fetId && incId === fetId && !hasLabel(fetchedVal) && hasLabel(incoming)) {
              fetched[key] = incoming;
            } else if (incoming && incId && !fetId && hasLabel(incoming)) {
              fetched[key] = incoming;
            }
          };
          mergeBankRef(incomingBank, fetched.bankaccount, "bankaccount");
          mergeBankRef(incomingBankTo, fetched.bank_account_to, "bank_account_to");
          p = fetched;
        }
      } catch (e) {
        console.error("QuickMarkPaymentPaidModal fetch payment:", e);
      }

      await this.loadExpenseAmountCap(p);

      this.selectedPayment = p;
      const amt = p.amount != null && p.amount !== "" ? Number(p.amount) : NaN;
      const amountStr = Number.isFinite(amt) ? String(amt) : "";
      const estStr = toYyyyMmDdSync(p.estimated_paymentdate || "");
      const payStr = this.formatPaymentDateForInput(p);
      const todayStr = calendarTodayLocalYyyyMmDd();
      let payment_date = "";
      let estimated_paymentdate = "";
      if (estStr) {
        estimated_paymentdate = estStr;
        payment_date = "";
      } else {
        payment_date = payStr || todayStr;
        estimated_paymentdate = "";
      }
      this.form = {
        amountStr,
        payment_date,
        estimated_paymentdate,
        bankaccount: this.bankFromPayment(p, "bankaccount"),
        bank_account_to: this.bankAccountToField
          ? this.bankFromPayment(p, "bank_account_to")
          : "",
        comment: p.comment || "",
        payment_request_file: this.normalizePaymentRequestFileList(
          p.payment_request_file
        ),
        lineRoundingStr: lineExtraToStr(this.singleAppliedToLine?.rounding),
        lineCommissionStr: lineExtraToStr(
          this.singleAppliedToLine?.bank_commission
        ),
      };
      this.initialSnapshot = {
        payment_date: this.form.payment_date,
        estimated_paymentdate: this.form.estimated_paymentdate,
        bankaccountId: bankRefId(this.form.bankaccount),
        bankAccountToId: bankRefId(this.form.bank_account_to),
        comment: this.form.comment || "",
        payment_request_file: [...this.form.payment_request_file],
        lineRoundingStr: this.form.lineRoundingStr,
        lineCommissionStr: this.form.lineCommissionStr,
      };

      this.visible = true;
    },
    closeModal() {
      this.visible = false;
      this.selectedPayment = null;
      this.expenseAmountCap = null;
      this.editMode = false;
      this.bankAccountToField = null;
      this.form = {
        amountStr: "",
        payment_date: "",
        estimated_paymentdate: "",
        bankaccount: "",
        bank_account_to: "",
        comment: "",
        payment_request_file: [],
      };
      this.initialSnapshot = null;
      this.errors = {};
      this.serverValidationError = "";
      this.deleting = false;
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    onBankAccountChange(val) {
      this.form.bankaccount = val;
      if (this.errors.bankaccount) delete this.errors.bankaccount;
    },
    onBankAccountToChange(val) {
      this.form.bank_account_to = val;
      if (this.errors.bank_account_to) delete this.errors.bank_account_to;
    },
    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.stages.quickMarkPaymentPaidFileTooBig"),
        });
        return;
      }
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.stages.quickMarkPaymentPaidFileType"),
        });
        return;
      }
      try {
        this.uploadingFile = true;
        this.uploadProgress = 0;
        const formData = new FormData();
        formData.append("image", file);
        const response = await wencesApi.post("/api/upload/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (pe) => {
            this.uploadProgress = Math.round((pe.loaded * 100) / (pe.total || 1));
          },
        });
        if (response.data && response.data.fileKey) {
          this.form.payment_request_file = [response.data.fileKey];
        } else {
          throw new Error((response.data && response.data.message) || "upload failed");
        }
      } catch (e) {
        console.error(e);
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: this.$tn("financialSummary.stages.quickMarkPaymentPaidFileError"),
        });
      } finally {
        this.uploadingFile = false;
        this.uploadProgress = 0;
      }
    },
    removeFile() {
      this.form.payment_request_file = [];
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    getFileName(file) {
      if (typeof file === "string") return file.split("/").pop() || file;
      return file?.name || "—";
    },
    async removePayment() {
      if (!this.editMode) return;
      if (!this.selectedPayment || !this.selectedPayment._id) return;
      if (this.saving || this.deleting) return;
      if (!this.canDeleteSelectedPayment) return;
      this.deleting = true;
      this.serverValidationError = "";
      try {
        const { deleted, error } = await confirmAndDeletePayment(
          (k) => this.$t(k),
          this.selectedPayment
        );
        if (error) this.serverValidationError = error;
        if (!deleted) return;
        this.closeModal();
        this.$emit("saved");
      } finally {
        this.deleting = false;
      }
    },
    async submitQuickPay() {
      this.errors = {};
      this.serverValidationError = "";
      if (!this.selectedPayment || !this.selectedPayment._id) return;
      const amountNum = parseAmountInput(this.form.amountStr);
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        this.errors.amount = this.$t("financialSummary.stages.quickMarkPaymentPaidAmountInvalid");
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.stages.quickMarkPaymentPaidValidationTitle"),
          text: this.errors.amount,
        });
        return;
      }
      if (this.expenseAmountCap != null && Number.isFinite(this.expenseAmountCap)) {
        if (amountNum > this.expenseAmountCap + 1e-6) {
          this.errors.amount = this.$t("financialSummary.stages.quickMarkPaymentPaidAmountExceedsExpense", {
            max: this.formatMoneyHint(this.expenseAmountCap),
          });
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.quickMarkPaymentPaidValidationTitle"),
            text: this.errors.amount,
          });
          return;
        }
      }
      if (this.editMode) {
        if (this.showBankAccountField && this.selectedPaymentIsPaid && !bankRefId(this.form.bankaccount)) {
          this.errors.bankaccount = this.$t("financialSummary.stages.quickMarkPaymentPaidBankRequired");
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.quickMarkPaymentPaidValidationTitle"),
            text: this.errors.bankaccount,
          });
          return;
        }
        const payload = {
          partialUpdate: true,
          amount: amountNum,
        };
        const commentTrim =
          this.form.comment != null ? String(this.form.comment).trim() : "";
        if (
          commentTrim ||
          (this.initialSnapshot && String(this.initialSnapshot.comment || "").trim())
        ) {
          payload.comment = commentTrim;
        }
        const bankId = bankRefId(this.form.bankaccount);
        if (bankId) {
          payload.bankaccount = bankId;
        }
        if (this.bankAccountToField) {
          payload.bank_account_to = bankRefId(this.form.bank_account_to) || null;
        }
        if (
          this.initialSnapshot &&
          JSON.stringify(this.form.payment_request_file) !==
            JSON.stringify(this.initialSnapshot.payment_request_file)
        ) {
          payload.payment_request_file = this.form.payment_request_file;
        }
        const appliedToEdit = this.buildAppliedToForPut();
        if (appliedToEdit) {
          payload.appliedto = appliedToEdit;
        }
        this.saving = true;
        try {
          await wencesApi.put(
            `/api/gotrading/dynamic/payment/${this.selectedPayment._id}`,
            payload,
            { timeout: 120000 }
          );
          await Swal.fire({
            icon: "success",
            title: this.$tn("success"),
            timer: 1800,
            showConfirmButton: false,
          });
          this.closeModal();
          this.$emit("saved");
        } catch (error) {
          console.error("QuickMarkPaymentPaidModal edit save:", error);
          if (isPaymentUpdateTransportError(error)) {
            const persisted = await verifyPaymentStatusPersisted(
              wencesApi,
              this.selectedPayment._id,
              {
                paid: this.selectedPaymentIsPaid,
                bankaccount: this.form.bankaccount,
              }
            );
            if (persisted) {
              await Swal.fire({
                icon: "success",
                title: this.$tn("success"),
                timer: 1800,
                showConfirmButton: false,
              });
              this.closeModal();
              this.$emit("saved");
              return;
            }
          }
          let msg = this.$t("financialSummary.stages.quickMarkPaymentPaidSaveError");
          if (error.response && error.response.data) {
            msg = error.response.data.message || error.response.data.error || msg;
          }
          this.serverValidationError = msg;
          await Swal.fire({
            icon: "error",
            title: this.$tn("error"),
            text: msg,
          });
        } finally {
          this.saving = false;
        }
        return;
      }
      const hasPay = !!(this.form.payment_date && String(this.form.payment_date).trim());
      const hasEst = !!(this.form.estimated_paymentdate && String(this.form.estimated_paymentdate).trim());
      const estimatedOnly = hasEst;

      if (!hasPay && !hasEst) {
        const msg = this.$t("financialSummary.stages.quickMarkPaymentPaidNeitherDateError");
        this.errors.payment_date = msg;
        await Swal.fire({
          icon: "warning",
          title: this.$tn("financialSummary.stages.quickMarkPaymentPaidValidationTitle"),
          text: msg,
        });
        return;
      }

      if (!estimatedOnly) {
        if (!hasPay) {
          this.errors.payment_date = this.$t("financialSummary.stages.quickMarkPaymentPaidDateRequired");
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.quickMarkPaymentPaidValidationTitle"),
            text: this.errors.payment_date,
          });
          return;
        }
        if (!bankRefId(this.form.bankaccount)) {
          this.errors.bankaccount = this.$t("financialSummary.stages.quickMarkPaymentPaidBankRequired");
          await Swal.fire({
            icon: "warning",
            title: this.$tn("financialSummary.stages.quickMarkPaymentPaidValidationTitle"),
            text: this.errors.bankaccount,
          });
          return;
        }
        if (this.bankAccountToField) {
          const fromId = bankRefId(this.form.bankaccount);
          const toId = bankRefId(this.form.bank_account_to);
          if (fromId && toId && fromId === toId) {
            const msg = this.$t(
              "Bank account (from) and Bank account (to) cannot be the same"
            );
            this.errors.bank_account_to = msg;
            await Swal.fire({
              icon: "warning",
              title: this.$tn("financialSummary.stages.quickMarkPaymentPaidValidationTitle"),
              text: msg,
            });
            return;
          }
        }
      }
      let payload;
      if (estimatedOnly) {
        payload = {
          partialUpdate: true,
          paid: false,
          amount: amountNum,
          payment_date: null,
          estimated_paymentdate: this.form.estimated_paymentdate,
        };
      } else {
        payload = {
          partialUpdate: true,
          paid: true,
          amount: amountNum,
          payment_date: this.form.payment_date,
          estimated_paymentdate: null,
          bankaccount: bankRefId(this.form.bankaccount) || this.form.bankaccount,
        };
        if (this.bankAccountToField) {
          payload.bank_account_to =
            bankRefId(this.form.bank_account_to) || this.form.bank_account_to || null;
        }
      }
      const commentTrim =
        this.form.comment != null ? String(this.form.comment).trim() : "";
      if (commentTrim) {
        payload.comment = commentTrim;
      }
      if (
        this.initialSnapshot &&
        JSON.stringify(this.form.payment_request_file) !==
          JSON.stringify(this.initialSnapshot.payment_request_file)
      ) {
        payload.payment_request_file = this.form.payment_request_file;
      }
      const appliedToEdit = this.buildAppliedToForPut();
      if (appliedToEdit) {
        payload.appliedto = appliedToEdit;
      }

      this.saving = true;
      try {
        await wencesApi.put(
          `/api/gotrading/dynamic/payment/${this.selectedPayment._id}`,
          payload,
          { timeout: 120000 }
        );
        await Swal.fire({
          icon: "success",
          title: this.$tn("financialSummary.stages.quickMarkPaymentPaidSuccessTitle"),
          text: estimatedOnly
            ? this.$tn("financialSummary.stages.quickMarkPaymentPaidSuccessEstimated")
            : this.$tn("financialSummary.stages.quickMarkPaymentPaidSuccessText"),
          timer: 2200,
          showConfirmButton: false,
        });
        this.closeModal();
        this.$emit("saved");
      } catch (error) {
        console.error("QuickMarkPaymentPaidModal save:", error);
        if (isPaymentUpdateTransportError(error)) {
          const intended = estimatedOnly
            ? { paid: false }
            : {
                paid: true,
                payment_date: this.form.payment_date,
                bankaccount: this.form.bankaccount,
              };
          const persisted = await verifyPaymentStatusPersisted(
            wencesApi,
            this.selectedPayment._id,
            intended
          );
          if (persisted) {
            await Swal.fire({
              icon: "success",
              title: this.$tn("financialSummary.stages.quickMarkPaymentPaidSuccessTitle"),
              text: estimatedOnly
                ? this.$tn("financialSummary.stages.quickMarkPaymentPaidSuccessEstimated")
                : this.$tn("financialSummary.stages.quickMarkPaymentPaidSuccessText"),
              timer: 2200,
              showConfirmButton: false,
            });
            this.closeModal();
            this.$emit("saved");
            return;
          }
        }
        let msg = this.$t("financialSummary.stages.quickMarkPaymentPaidSaveError");
        if (error.response && error.response.data) {
          msg = error.response.data.message || error.response.data.error || msg;
        }
        this.serverValidationError = msg;
        await Swal.fire({
          icon: "error",
          title: this.$tn("error"),
          text: msg,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
